import React, { useState, useEffect } from 'react';
import { ref, get, update, remove } from 'firebase/database';
import { database } from '../firebase/config';
import { formattaData } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import FatturaPDF from '../pdf/FatturaPDF';
import FatturazioneForm from './FatturazioneForm';

function fmtCHF(num) {
  const n = Number(num) || 0;
  const negativo = n < 0;
  const numeroAbs = Math.abs(n).toFixed(2);
  const [parteIntera, parteDecimale] = numeroAbs.split('.');
  const parteInteraFormattata = parteIntera.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return (negativo ? '-' : '') + parteInteraFormattata + '.' + parteDecimale;
}

export default function ListaFatture({ onBack }) {
  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clienti, setClienti] = useState({});
  const [cantieri, setCantieri] = useState({});
  const [fatturaInModifica, setFatturaInModifica] = useState(null);

  function handleModifica(fattura) {
    setFatturaInModifica(fattura);
  }

  function handleChiudiModifica() {
    setFatturaInModifica(null);
    caricaDati();
  }

  async function caricaDati() {
      const [fattureSnap, clientiSnap, cantieriSnap] = await Promise.all([
        get(ref(database, 'fatture')),
        get(ref(database, 'clienti')),
        get(ref(database, 'cantieri')),
      ]);

      // Clienti
      if (clientiSnap.exists()) {
        const cli = {};
        Object.entries(clientiSnap.val()).forEach(([id, v]) => {
          cli[id] = v.ragioneSociale || id;
        });
        setClienti(cli);
      }

      // Cantieri
      if (cantieriSnap.exists()) {
        const cant = {};
        Object.entries(cantieriSnap.val()).forEach(([id, v]) => {
          cant[id] = v.luogo || id;
        });
        setCantieri(cant);
      }

      // Fatture
      if (fattureSnap.exists()) {
        const lista = Object.entries(fattureSnap.val()).map(([id, v]) => ({
          id,
          ...v,
        }));
        lista.sort((a, b) => (b.dataFattura || '').localeCompare(a.dataFattura || ''));
        setFatture(lista);
      } else {
        setFatture([]);
      }
      setLoading(false);
  }

  useEffect(() => {
    caricaDati();
  }, []);

  async function toggleSpedita(id, attuale) {
    await update(ref(database, `fatture/${id}`), {
      spedita: !attuale,
    });
    // Aggiorna locale
    setFatture((prev) =>
      prev.map((f) => (f.id === id ? { ...f, spedita: !attuale } : f))
    );
  }

  async function togglePagata(id, attuale) {
    await update(ref(database, `fatture/${id}`), {
      pagata: !attuale,
    });
    setFatture((prev) =>
      prev.map((f) => (f.id === id ? { ...f, pagata: !attuale } : f))
    );
  }


  async function apriPdfFattura(fattura) {
    try {
      const [cliSnap, cantSnap] = await Promise.all([
        get(ref(database, `clienti/${fattura.clienteId}`)),
        fattura.cantiereId ? get(ref(database, `cantieri/${fattura.cantiereId}`)) : Promise.resolve(null),
      ]);

      const clienteObj = cliSnap.exists() ? cliSnap.val() : null;
      const cantiereObj = cantSnap && cantSnap.exists() ? cantSnap.val() : null;

      const blob = await pdf(
        <FatturaPDF
          fattura={fattura}
          cliente={clienteObj}
          cantiere={cantiereObj}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Errore apertura PDF fattura:', err);
      alert('Errore apertura PDF fattura: ' + err.message);
    }
  }


  async function eliminaFattura(fattura) {
    const conferma = window.confirm(
      `Eliminare definitivamente la fattura n° ${fattura.numeroFattura || '-'}?\n\n` +
      'Le bolle e i preventivi associati verranno nuovamente resi disponibili per la fatturazione.\n\n' +
      'ATTENZIONE: l’operazione non può essere annullata.'
    );

    if (!conferma) return;

    try {
      const righe = fattura.righe || [];

      // 1. Libera le bolle
      const bolleId = [...new Set(
        righe
          .map((riga) => riga.bollaId)
          .filter(Boolean)
      )];

      // 2. Libera i preventivi
      const preventiviId = [...new Set(
        righe
          .map((riga) => riga.preventivoId)
          .filter(Boolean)
      )];

      const aggiornamenti = {};

      bolleId.forEach((bollaId) => {
        aggiornamenti[`bolleForm/${bollaId}/fatturata`] = false;
        aggiornamenti[`bolleForm/${bollaId}/numeroFatturaAssociata`] = null;
        aggiornamenti[`bolleForm/${bollaId}/dataFatturaAssociata`] = null;
        aggiornamenti[`bolleForm/${bollaId}/fatturaIdAssociata`] = null;
      });

      preventiviId.forEach((preventivoId) => {
        aggiornamenti[`preventivi/${preventivoId}/fatturato`] = false;
        aggiornamenti[`preventivi/${preventivoId}/numeroFatturaAssociata`] = null;
        aggiornamenti[`preventivi/${preventivoId}/dataFatturaAssociata`] = null;
        aggiornamenti[`preventivi/${preventivoId}/fatturaIdAssociata`] = null;
      });

      // Prima libera bolle e preventivi, poi elimina la fattura.
      if (Object.keys(aggiornamenti).length > 0) {
        await update(ref(database, '/'), aggiornamenti);
      }

      await remove(ref(database, `fatture/${fattura.id}`));

      setFatture((prev) => prev.filter((f) => f.id !== fattura.id));
      alert('Fattura eliminata e bolle/preventivi associati resi nuovamente disponibili.');
    } catch (error) {
      console.error('Errore eliminazione fattura:', error);
      alert('Errore durante l’eliminazione: ' + error.message);
    }
  }

  if (fatturaInModifica) {
    return (
      <FatturazioneForm
        fatturaDaModificare={fatturaInModifica}
        onBack={handleChiudiModifica}
      />
    );
  }

return (
  <div className="lista-container lista-fatture-container">

<div className="fatturazione-header">
  <button
    type="button"
    className="btn-logout"
    onClick={onBack}
  >
    Torna indietro
  </button>
</div>

      <div className="lista-header">
        <h2>📋 Lista fatture</h2>
      </div>

      {loading ? (
        <p className="loading-text">Caricamento...</p>
      ) : fatture.length === 0 ? (
        <div className="empty-state">
          <p>Nessuna fattura presente.</p>
        </div>
      ) : (
        <>

          {/* Desktop */}
          <div className="lista-fatture-table-wrapper">
            <table className="data-table desktop-only lista-fatture-table">
              <thead>
                <tr>
                  <th>Numero fattura</th>
                  <th>Data FATTURA</th>
                  <th>Numero BOLLA</th>
                  <th>Data BOLLA</th>
                  <th>Cliente</th>
                  <th>Cantiere</th>
                  <th>Totale CHF</th>
                  <th>Spedita</th>
                  <th>Pagata</th>
                  <th>PDF</th>
                  <th>Modifica</th>
                  <th>Azioni</th>
                </tr>
              </thead>

              <tbody>
                {fatture.map((f) => {
                  const clienteLabel = clienti[f.clienteId] || '-';
                  const cantiereLabel = cantieri[f.cantiereId] || '-';

                  const numeriBolla = [...new Set(
                    (f.righe || [])
                      .map((riga) => riga.numeroBolla)
                      .filter(Boolean)
                  )].join(', ') || '-';

                  const dateBolla = [...new Set(
                    (f.righe || [])
                      .map((riga) => riga.data)
                      .filter(Boolean)
                  )].map((dataBolla) => formattaData(dataBolla)).join(', ') || '-';

                  return (
                    <tr key={f.id}>
                      <td><strong>{f.numeroFattura || '-'}</strong></td>
                      <td>{formattaData(f.dataFattura)}</td>
                      <td>{numeriBolla}</td>
                      <td>{dateBolla}</td>
                      <td>{clienteLabel}</td>
                      <td>{cantiereLabel}</td>
                      <td style={{ textAlign: 'center' }}>{fmtCHF(f.totale)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={f.spedita ? 'btn-success small' : 'btn-danger small'}
                          onClick={() => toggleSpedita(f.id, f.spedita)}
                        >
                          {f.spedita ? 'Sì' : 'No'}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={f.pagata ? 'btn-success small' : 'btn-danger small'}
                          onClick={() => togglePagata(f.id, f.pagata)}
                        >
                          {f.pagata ? 'Sì' : 'No'}
                        </button>
                      </td>


                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-edit"
                          type="button"
                          onClick={() => apriPdfFattura(f)}
                          title="Visualizza fattura PDF"
                          aria-label={`Visualizza PDF fattura ${f.numeroFattura || ''}`}
                        >
                          <img
                            src="/icon-pdf.svg"
                            alt="PDF"
                            style={{ width: '30px', display: 'block' }}
                          />
                        </button>
                      </td>

<td style={{ textAlign: 'center' }}>
  <button
    className="btn-secondary small"
    type="button"
    onClick={() => handleModifica(f)}
    title="Modifica fattura"
    aria-label={`Modifica fattura ${f.numeroFattura || ''}`}
  >
    Modifica
  </button>
</td>

<td style={{ textAlign: 'center' }}>
  <button
    className="btn-delete"
    onClick={() => eliminaFattura(f)}
    title="Elimina fattura"
    aria-label={`Elimina fattura ${f.numeroFattura || ''}`}
  >
    <img
      src="/icon-delete.svg"
      alt="Elimina"
      style={{ width: '28px', display: 'block' }}
    />
  </button>
</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        {/* Mobile */}
        <div className="mobile-cards">
          {fatture.map((f) => (
            <div key={f.id} className="mobile-card">
              <div className="mobile-card-header">
                Fattura {f.numeroFattura || '-'}
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Data fattura:</span>
                <span className="mobile-card-value">
                  {formattaData(f.dataFattura)}
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Bolla:</span>
                <span className="mobile-card-value">
                  {[...new Set(
                    (f.righe || [])
                      .map((riga) => riga.numeroBolla)
                      .filter(Boolean)
                  )].join(', ') || '-'}
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Data bolla:</span>
                <span className="mobile-card-value">
                  {[...new Set(
                    (f.righe || [])
                      .map((riga) => riga.data)
                      .filter(Boolean)
                  )].map((dataBolla) => formattaData(dataBolla)).join(', ') || '-'}
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Cliente:</span>
                <span className="mobile-card-value">
                  {clienti[f.clienteId] || '-'}
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Cantiere:</span>
                <span className="mobile-card-value">
                  {cantieri[f.cantiereId] || '-'}
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Totale:</span>
                <span className="mobile-card-value">
                  <strong>CHF {fmtCHF(f.totale)}</strong>
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Spedita:</span>
                <span className="mobile-card-value">
                  <button
                    className={f.spedita ? 'btn-success small' : 'btn-danger small'}
                    onClick={() => toggleSpedita(f.id, f.spedita)}
                  >
                    {f.spedita ? 'Sì' : 'No'}
                  </button>
                </span>
              </div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Pagata:</span>
                <span className="mobile-card-value">
                  <button
                    className={f.pagata ? 'btn-success small' : 'btn-danger small'}
                    onClick={() => togglePagata(f.id, f.pagata)}
                  >
                    {f.pagata ? 'Sì' : 'No'}
                  </button>
                </span>
              </div>

<div className="mobile-card-row">
  <span className="mobile-card-label">PDF</span>
  <span className="mobile-card-value">
    <button
      className="btn-edit"
      type="button"
      onClick={() => apriPdfFattura(f)}
      title="Visualizza fattura PDF"
    >
      <img src="/icon-pdf.svg" alt="PDF" style={{ width: 30, display: 'block' }} />
    </button>
  </span>
</div>

<div className="mobile-card-row">
  <span className="mobile-card-label">Modifica</span>
  <span className="mobile-card-value">
    <button
      className="btn-secondary small"
      type="button"
      onClick={() => handleModifica(f)}
      title="Modifica fattura"
    >
      Modifica
    </button>
  </span>
</div>

              <div className="mobile-card-row">
                <span className="mobile-card-label">Azioni:</span>
                <span className="mobile-card-value">
                  <button
                    className="btn-delete"
                    onClick={() => eliminaFattura(f)}
                    title="Elimina fattura"
                    aria-label={`Elimina fattura ${f.numeroFattura || ''}`}
                  >
                    <img
                      src="/icon-delete.svg"
                      alt="Elimina"
                      style={{ width: '28px', display: 'block' }}
                    />
                  </button>
                </span>
              </div>

            </div>
          ))}
        </div>        </>
      )}
    </div>
  );
}